import { useEffect, useRef, type ReactNode } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title?: string;
  readonly children: ReactNode;
  readonly size?: ModalSize;
  /** Defaults `true`. Set false to disable click-outside-to-close. */
  readonly closeOnBackdropClick?: boolean;
  /** Defaults `true`. Set false to disable Esc-to-close. */
  readonly closeOnEsc?: boolean;
  /** Defaults `true`. Hides the X button when false. */
  readonly showCloseButton?: boolean;
  readonly className?: string;
  readonly footer?: ReactNode;
}

/**
 * Centered modal dialog with backdrop, Esc-to-close, click-outside-to-close,
 * and a body-scroll-lock side effect. Mirrors HTHD's customer + admin Modal
 * prop shapes (`isOpen`, `onClose`, `title`, `size`, `children`).
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = '',
  footer,
}: ModalProps): JSX.Element | null {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent): void => {
      if (closeOnEsc && event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, closeOnEsc, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (closeOnBackdropClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  const dialogCls = [
    'ferro-ui-modal__dialog',
    `ferro-ui-modal__dialog--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={overlayRef}
      className="ferro-ui-modal"
      onClick={handleBackdropClick}
      data-testid="ferro-modal-backdrop"
    >
      <div
        className={dialogCls}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'ferro-modal-title' : undefined}
      >
        {(title || showCloseButton) ? (
          <header className="ferro-ui-modal__header">
            {title ? (
              <h2 id="ferro-modal-title" className="ferro-ui-modal__title">
                {title}
              </h2>
            ) : <span />}
            {showCloseButton ? (
              <button
                type="button"
                className="ferro-ui-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            ) : null}
          </header>
        ) : null}
        <div className="ferro-ui-modal__body">{children}</div>
        {footer ? <footer className="ferro-ui-modal__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}
